/**
 * Compensation (`/api/admin/v2/hr/comp`) and the caller's own pay
 * (`/api/admin/v2/workplace/pay`). `comp.view` reads, `comp.manage` proposes,
 * `comp.approve` decides (never your own proposal) and records existing pay.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { Bonus, BonusKind, CompChange, CompHistory, CompReason, CompSummary, PayFrequency } from '@/client/types/hr';
import { HR_BASE, WORKPLACE_BASE, hrKeys, useHrApi } from './people';

const COMP_BASE = `${HR_BASE}/comp`;

export const compKeys = {
  all: [...hrKeys.all, 'comp'] as const,
  summary: () => [...compKeys.all, 'summary'] as const,
  history: (id: string | null | undefined) => [...compKeys.all, 'history', id] as const,
  mine: () => [...hrKeys.workplace(), 'pay'] as const,
};

export const useCompSummaryQuery = (enabled = true) => {
  const api = useHrApi(COMP_BASE);
  return useQuery({
    queryKey: compKeys.summary(),
    queryFn: () => api<CompSummary>('/summary'),
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCompHistoryQuery = (employeeId: string | null | undefined, enabled = true) => {
  const api = useHrApi(COMP_BASE);
  return useQuery({
    queryKey: compKeys.history(employeeId),
    queryFn: () => api<CompHistory>(`/employees/${encodeURIComponent(employeeId as string)}`),
    enabled: Boolean(employeeId) && enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useMyPayQuery = () => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({ queryKey: compKeys.mine(), queryFn: () => api<CompHistory>('/pay'), staleTime: 30_000, gcTime: DEFAULT_GC_TIME });
};

export type CompChangeInput = {
  employeeId: string;
  reason: CompReason;
  amount: number;
  currency: string;
  frequency: PayFrequency;
  effectiveOn: string;
  levelId?: string | null;
  reviewId?: string | null;
  note?: string;
};

export type BonusInput = {
  employeeId: string;
  kind: BonusKind;
  amount: number;
  currency: string;
  payableOn?: string | null;
  reviewId?: string | null;
  note?: string;
};

type Decision = { id: string; decision: 'approve' | 'reject'; note?: string };

function useCompMutation<TIn, TOut>(fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(COMP_BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TIn) => fn(api, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
}

/** `record: true` enters existing pay (hire / correction) as approved — `comp.approve`. */
export const useProposeCompChangeMutation = () =>
  useCompMutation(
    (api, { record, ...body }: CompChangeInput & { record?: boolean }) =>
      api<CompChange>(record ? '/changes/record' : '/changes', 'POST', body),
  );

export const useDecideCompChangeMutation = () =>
  useCompMutation((api, { id, ...body }: Decision) => api<CompChange>(`/changes/${encodeURIComponent(id)}/decide`, 'POST', body));

export const useCancelCompChangeMutation = () =>
  useCompMutation((api, id: string) => api<CompChange>(`/changes/${encodeURIComponent(id)}/cancel`, 'POST'));

export const useProposeBonusMutation = () => useCompMutation((api, body: BonusInput) => api<Bonus>('/bonuses', 'POST', body));

export const useDecideBonusMutation = () =>
  useCompMutation((api, { id, ...body }: Decision) => api<Bonus>(`/bonuses/${encodeURIComponent(id)}/decide`, 'POST', body));

export const useBonusPaidMutation = () =>
  useCompMutation((api, id: string) => api<Bonus>(`/bonuses/${encodeURIComponent(id)}/paid`, 'POST'));

export const useCancelBonusMutation = () =>
  useCompMutation((api, id: string) => api<Bonus>(`/bonuses/${encodeURIComponent(id)}/cancel`, 'POST'));
